import { useMemo } from "react";
import { useParams, Link } from "react-router-dom";
import {
  ArrowLeft,
  ArrowUpRight,
  Briefcase,
  CheckCircle2,
  AlertTriangle,
  Sparkles,
  Flag,
  Layers3,
  BookOpen,
} from "lucide-react";
import { businessGuides } from "../businessGuides";

function getTagClasses(tag: string) {
  switch (tag) {
    case "Must Read":
      return "border-dtf-accent/25 bg-dtf-accent/10 text-dtf-accent";
    case "Money":
      return "border-green-500/25 bg-green-500/10 text-green-400";
    case "Beginner":
      return "border-dtf-blue/25 bg-dtf-blue/10 text-dtf-blue";
    case "Advanced":
      return "border-purple-500/25 bg-purple-500/10 text-purple-400";
    case "Workflow":
      return "border-cyan-500/25 bg-cyan-500/10 text-cyan-400";
    default:
      return "border-white/10 bg-black/20 text-dtf-text-muted";
  }
}

function BluePanel({
  children,
  className = "",
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div
      className={[
        "relative overflow-hidden rounded-[28px] border border-dtf-accent/20",
        "bg-[radial-gradient(circle_at_top_center,rgba(37,99,235,0.18),transparent_42%),linear-gradient(180deg,rgba(7,16,35,0.98)_0%,rgba(6,14,30,0.98)_100%)]",
        "shadow-[0_0_0_1px_rgba(245,158,11,0.05),0_0_34px_rgba(37,99,235,0.16)]",
        className,
      ].join(" ")}
    >
      <div className="pointer-events-none absolute inset-0 bg-[linear-gradient(90deg,rgba(37,99,235,0.08),transparent_30%,transparent_70%,rgba(37,99,235,0.06))]" />
      <div className="pointer-events-none absolute inset-0 rounded-[28px] ring-1 ring-inset ring-white/5" />
      {children}
    </div>
  );
}

function SectionCard({
  icon,
  title,
  children,
  className = "",
}: {
  icon: React.ReactNode;
  title: string;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <BluePanel className={className}>
      <div className="p-6 md:p-7">
        <div className="mb-4 flex items-center gap-3">
          <div className="flex h-11 w-11 items-center justify-center rounded-2xl border border-white/10 bg-black/20 text-dtf-accent">
            {icon}
          </div>
          <h2 className="text-2xl font-bold text-white">{title}</h2>
        </div>

        {children}
      </div>
    </BluePanel>
  );
}

export default function BusinessGuideDetail() {
  const { id } = useParams();
  const guide = businessGuides.find((g) => g.id === id);

  const relatedGuides = useMemo(() => {
    if (!guide) return [];

    const sameCategory = businessGuides.filter(
      (g) => g.id !== guide.id && g.category === guide.category
    );

    const sameTags = businessGuides.filter(
      (g) =>
        g.id !== guide.id &&
        g.category !== guide.category &&
        g.tags?.some((tag) => guide.tags?.includes(tag))
    );

    return [...sameCategory, ...sameTags].slice(0, 3);
  }, [guide]);

  if (!guide) {
    return (
      <div className="mx-auto max-w-5xl px-4 py-8 text-white sm:px-6 lg:px-8">
        <BluePanel>
          <div className="p-6 md:p-8">
            <h1 className="mb-3 text-2xl font-bold">Guide not found</h1>
            <p className="mb-5 text-dtf-text-muted">
              This business guide could not be found. It may have been moved,
              renamed, or removed from the current guide library.
            </p>

            <Link
              to="/business-builder"
              className="inline-flex items-center gap-2 text-dtf-blue hover:text-white"
            >
              <ArrowLeft className="h-4 w-4" />
              Back to Business Builder
            </Link>
          </div>
        </BluePanel>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-6xl px-4 py-8 text-white sm:px-6 lg:px-8">
      <Link
        to="/business-builder"
        className="mb-6 inline-flex items-center gap-2 text-sm text-dtf-text hover:text-white"
      >
        <ArrowLeft className="h-4 w-4" />
        Back to Business Builder
      </Link>

      <BluePanel className="mb-6">
        <div className="p-6 md:p-8">
          <div className="flex flex-col gap-6 md:flex-row md:items-start">
            <div className="flex h-16 w-16 shrink-0 items-center justify-center rounded-[22px] border border-white/10 bg-black/20 text-dtf-accent">
              <Briefcase className="h-8 w-8" />
            </div>

            <div className="min-w-0 flex-1">
              <div className="mb-3 flex flex-wrap items-center gap-2">
                <span className="inline-flex items-center rounded-full border border-dtf-accent/20 bg-black/20 px-3 py-1 text-xs font-semibold uppercase tracking-[0.16em] text-dtf-accent">
                  {guide.category}
                </span>

                {(guide.tags ?? []).map((tag) => (
                  <span
                    key={tag}
                    className={`rounded-full border px-2.5 py-1 text-xs font-medium ${getTagClasses(tag)}`}
                  >
                    {tag}
                  </span>
                ))}
              </div>

              <h1 className="mb-3 text-3xl font-extrabold leading-tight tracking-tight text-white md:text-5xl">
                {guide.title}
              </h1>

              <p className="max-w-4xl text-lg leading-8 text-dtf-text">
                {guide.summary}
              </p>
            </div>
          </div>
        </div>
      </BluePanel>

      <div className="mb-6 grid gap-4 md:grid-cols-2">
        <SectionCard
          icon={<Sparkles className="h-5 w-5" />}
          title="Overview"
        >
          <p className="leading-8 text-dtf-text">{guide.content.overview}</p>
        </SectionCard>

        <SectionCard
          icon={<Flag className="h-5 w-5" />}
          title="Why This Matters"
        >
          <p className="leading-8 text-dtf-text">{guide.content.whyItMatters}</p>
        </SectionCard>
      </div>

      <SectionCard
        icon={<CheckCircle2 className="h-5 w-5" />}
        title="Action Steps"
        className="mb-6"
      >
        <div className="space-y-4">
          {guide.content.steps.map((step, index) => (
            <div
              key={index}
              className="rounded-[24px] border border-white/10 bg-black/20 p-4 md:p-5"
            >
              <div className="mb-3 flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-full border border-dtf-accent/20 bg-dtf-accent/10 text-sm font-bold text-dtf-accent">
                  {index + 1}
                </div>
                <div className="text-sm font-semibold uppercase tracking-[0.18em] text-dtf-accent">
                  Step {index + 1}
                </div>
              </div>

              <p className="leading-8 text-dtf-text">{step}</p>
            </div>
          ))}
        </div>
      </SectionCard>

      <SectionCard
        icon={<AlertTriangle className="h-5 w-5" />}
        title="Common Mistakes"
        className="mb-6"
      >
        <div className="space-y-3">
          {guide.content.mistakes.map((mistake, index) => (
            <div
              key={index}
              className="rounded-[22px] border border-red-500/15 bg-red-500/5 p-4"
            >
              <p className="leading-8 text-dtf-text">{mistake}</p>
            </div>
          ))}
        </div>
      </SectionCard>

      <div className="mb-6 grid gap-4 md:grid-cols-2">
        <SectionCard
          icon={<Sparkles className="h-5 w-5" />}
          title="Pro Tip"
        >
          <p className="leading-8 text-white">{guide.content.proTip}</p>
        </SectionCard>

        <SectionCard
          icon={<BookOpen className="h-5 w-5" />}
          title="Bottom Line"
        >
          <p className="leading-8 text-dtf-text">{guide.content.bottomLine}</p>
        </SectionCard>
      </div>

      {relatedGuides.length > 0 && (
        <>
          <BluePanel className="mb-4">
            <div className="flex items-center justify-between gap-4 px-6 py-5">
              <div>
                <div className="text-sm text-dtf-text-muted">Keep going</div>
                <div className="text-2xl font-bold text-white">
                  Related Guides
                </div>
              </div>

              <div className="rounded-full border border-dtf-blue/25 bg-dtf-blue/10 px-4 py-2 text-sm font-semibold uppercase tracking-[0.18em] text-dtf-blue">
                Recommended next reads
              </div>
            </div>
          </BluePanel>

          <div className="grid grid-cols-1 gap-5 md:grid-cols-3">
            {relatedGuides.map((related) => (
              <Link
                key={related.id}
                to={`/business-builder/${related.id}`}
                className="group relative flex h-full flex-col justify-between overflow-hidden rounded-[26px] border border-white/10 bg-[radial-gradient(circle_at_top_center,rgba(37,99,235,0.14),transparent_48%),linear-gradient(180deg,rgba(9,21,44,0.98)_0%,rgba(7,15,31,0.98)_100%)] p-5 transition duration-200 hover:-translate-y-0.5 hover:border-dtf-blue/30 hover:shadow-[0_0_26px_rgba(37,99,235,0.18)]"
              >
                <div className="pointer-events-none absolute inset-0 bg-[linear-gradient(90deg,rgba(37,99,235,0.06),transparent_35%,transparent_65%,rgba(37,99,235,0.04))]" />

                <div className="relative">
                  <div className="mb-4 flex items-start justify-between gap-4">
                    <div className="inline-flex items-center rounded-full border border-dtf-accent/20 bg-black/20 px-3 py-1 text-xs font-semibold uppercase tracking-[0.16em] text-dtf-accent">
                      {related.category}
                    </div>

                    <ArrowUpRight className="h-5 w-5 flex-shrink-0 text-dtf-text-muted transition-colors group-hover:text-dtf-blue" />
                  </div>

                  <h3 className="mb-3 text-xl font-bold leading-tight tracking-tight text-white transition-colors group-hover:text-dtf-blue">
                    {related.title}
                  </h3>

                  <p className="mb-5 text-sm leading-7 text-dtf-text">
                    {related.summary}
                  </p>

                  <div className="mb-4 flex flex-wrap gap-2">
                    {(related.tags ?? []).slice(0, 3).map((tag) => (
                      <span
                        key={tag}
                        className={`rounded-full border px-2.5 py-1 text-xs font-medium ${getTagClasses(tag)}`}
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>

                <div className="relative flex items-center justify-between border-t border-white/8 pt-4">
                  <span className="text-sm font-medium text-white transition-colors group-hover:text-dtf-blue">
                    Open Guide
                  </span>

                  <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.16em] text-dtf-text-muted transition-colors group-hover:text-dtf-blue">
                    <Layers3 className="h-3.5 w-3.5" />
                    Read Next
                  </span>
                </div>
              </Link>
            ))}
          </div>
        </>
      )}
    </div>
  );
}